"use client";

import { FC, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import Image from "next/image";
import { cn } from "@/lib/utils";
// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { formatBackgroundStyle } from "@/utils/styles/format";
import { useIsMobile } from "@/hooks/useIsMobile";
import RevealOnScroll from "@/components/atoms/RevealOnScroll";

/**
 * Section copy caps (enforced here in the widget, NOT in the CMS).
 * - Section header (pre_title + title) per the design spec: max 50 chars each line.
 * - Description: max 200 chars.
 */
const BRAND1_COPY_LIMITS = {
  header: 50,
  description: 200,
} as const;

function clampStr(s: string, max: number) {
  return s.length <= max ? s : s.slice(0, max);
}

/** One brand pill. Mobile image is optional and falls back to the desktop image. */
const BrandPillSchema = z.object({
  id: z.union([z.number(), z.string()]),
  meta_image: z.string().optional().default(""),
  meta_mobile_image: z.string().optional().default(""),
  meta_alt_image: z.string().optional().default(""),
  meta_priority: z.coerce.number().optional().default(0),
});

type BrandPill = z.infer<typeof BrandPillSchema>;

const Brand1DataSchema = z
  .object({
    // Section header line 1 (white) — capped at 50 chars.
    pre_title: z
      .string()
      .optional()
      .default("Solve real business challenges")
      .transform((s) => clampStr(String(s ?? "").trim(), BRAND1_COPY_LIMITS.header)),
    pre_title_color: z.string().optional().default("#FFFFFF"),
    // Section header line 2 (highlight) — capped at 50 chars.
    title: z
      .string()
      .optional()
      .default("for leading brands")
      .transform((s) => clampStr(String(s ?? "").trim(), BRAND1_COPY_LIMITS.header)),
    title_color: z.string().optional().default("#02A7B6"),
    description: z
      .string()
      .optional()
      .default("")
      .transform((s) => clampStr(String(s ?? "").trim(), BRAND1_COPY_LIMITS.description)),
    description_color: z.string().optional().default("rgba(255,255,255,0.7)"),
    desktop_background_color: z.string().optional().default("#000000"),
    mobile_background_color: z.string().optional().default("#000000"),
    backvariant: z
      .enum(["variant_1", "variant_2", "variant_3"])
      .optional()
      .default("variant_3"),
    font_style: z.string().optional().default("var(--font-google-sans)"),
    gradient_color_1: z.string().optional().default(""),
    gradient_color_2: z.string().optional().default(""),
    gradient_color_3: z.string().optional().default(""),
    gradient_color_4: z.string().optional().default(""),
    /** MySQL / API may send 0/1; coerce to boolean. */
    enable_animation: z
      .preprocess((v) => v === true || v === 1 || v === "1", z.boolean())
      .optional()
      .default(false),
    metadata: z.array(BrandPillSchema).optional().default([]),
  })
  .transform((data) => ({
    ...data,
    // Order pills by priority (ascending), like other card sections.
    metadata: [...(data.metadata ?? [])].sort(
      (a, b) => (a.meta_priority || 0) - (b.meta_priority || 0),
    ).slice(0, 8), // limit to 8 pills
  }));

type Brand1Data = z.infer<typeof Brand1DataSchema>;

/** Single pill — renders the mobile image (sm:hidden) and desktop image (hidden sm:block). */
const BrandPillCard: FC<{ pill: BrandPill }> = ({ pill }) => {
  // Mobile falls back to desktop; desktop falls back to mobile if a desktop image is missing.
  const desktopSrc = pill.meta_image || pill.meta_mobile_image;
  const mobileSrc = pill.meta_mobile_image || pill.meta_image;
  const alt = pill.meta_alt_image || "brand";

  return (
    <div
      className={cn(
        "flex items-center justify-center hover:bg-[rgba(255,255,255,0.1)] rounded-[10px] shrink-0",
        "transition-transform duration-300 ease-out hover:scale-[1.2] hover:-rotate-3",
        // Glassy border (Figma)
        "hover:border-[0.6px] hover:border-solid hover:border-[rgba(255,255,255,0.2)]",
        // Pill size per breakpoint (exact from Figma)
        "w-[160px] h-[60px]",
        "sm:w-[130px] sm:h-[56px]",
        "md:w-[140px] md:h-[60px]",
        "lg:w-[160px] lg:h-[70px]",
        "xl:w-[160px] xl:h-[70px]",
        // Inner padding (mobile py-12, desktop py-16)
        "px-[24px] py-[12px] sm:py-[16px]",
      )}
    >
      <div
        className={cn(
          "relative shrink-0",
          // Logo box per breakpoint (exact from Figma: ~90px wide on mobile, content-width on desktop)
          "w-[90px] h-[36px]",
          "sm:w-[82px] sm:h-[24px]",
          "md:w-[92px] md:h-[28px]",
          "lg:w-[112px] lg:h-[38px]",
          "xl:w-[112px] xl:h-[38px]",
        )}
      >
        {mobileSrc ? (
          <Image
            src={mobileSrc}
            alt={alt}
            fill
            sizes="112px"
            className="object-contain sm:hidden"
          />
        ) : null}
        {desktopSrc ? (
          <Image
            src={desktopSrc}
            alt={alt}
            fill
            sizes="112px"
            className="object-contain hidden sm:block"
          />
        ) : null}
      </div>
    </div>
  );
};

export const Brand1: FC<any> = (props) => {
  // Props can be { data } or { data: { data } } (admin preview) — extract the real data.
  const actualData = props?.data?.data || props?.data || props;

  const sectionRef = useRef<HTMLElement>(null);
  const pillsContainerRef = useRef<HTMLDivElement>(null);
  const [pillsInView, setPillsInView] = useState(false);
  const isMobile = useIsMobile();

  const parseResult = useMemo(
    () => Brand1DataSchema.safeParse(actualData),
    [actualData],
  );

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (sectionRef.current) {
  //     FireWidgetViewedEvent(sectionRef, "for_leading_brands");
  //   }
  // }, []);

  useEffect(() => {
    let timeoutRef: NodeJS.Timeout | null = null;
    
    const el = pillsContainerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          timeoutRef = setTimeout(() => {
            setPillsInView(true);
          }, 500);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -80px 0px" },
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      if (timeoutRef) {
        clearTimeout(timeoutRef);
      }
    }
  }, []);

  if (!parseResult.success) {
    console.error("❌ Brand1 Zod validation failed:", parseResult.error);
    return null;
  }

  const parsedData: Brand1Data = parseResult.data;
  const isAnimationEnabled = parsedData.enable_animation;
  const pills = parsedData.metadata.filter(
    (p) => p.meta_image || p.meta_mobile_image,
  );

  const bgColor = formatBackgroundStyle(parsedData, isMobile || false, "#000000");

  return (
    <section
      ref={sectionRef}
      className={cn(
        "relative w-full overflow-hidden flex justify-center",
        // Section padding per breakpoint (exact from Figma)
        "xs:mx-auto xs:px-[20px] xs:py-[40px]",
        "sm:px-[60px] sm:py-[60px]",
        "md:px-[80px] md:py-[60px]",
        "lg:px-[100px] lg:py-[60px]",
        "xl:px-[160px] xl:py-[80px]",
      )}
      style={bgColor}
    >
      <style>{`
        @keyframes brand1-pill-xz {
          from { transform: rotateY(-90deg); opacity: 0; }
          to   { transform: rotateY(0deg);   opacity: 1; }
        }
      `}</style>
      <div
        className={cn(
          "relative z-10 w-full flex",
          // Mobile: stacked + centered; Desktop: text-left / grid-right
          "flex-col items-center gap-[23px]",
          "sm:flex-row sm:items-center sm:justify-between sm:gap-[24px]",
        )}
      >
        {/* Text column */}
        <RevealOnScroll enableAnimation={isAnimationEnabled}>
          <div
            className={cn(
              "flex flex-col w-full",
              // Mobile: centered text; Desktop: left-aligned
              "text-center sm:text-left",
              "gap-[12px] sm:gap-[24px]",
              "sm:w-[340px] md:w-[380px] lg:w-[430px] xl:w-[520px]",
            )}
          >
            {/* Section header — line 1 (white) + line 2 (highlight) */}
            <div
              className={cn(
                "font-semibold leading-[1.4] [word-break:break-word]",
                "text-[20px] sm:text-[22px] md:text-[24px] lg:text-[28px] xl:text-[28px]",
              )}
              style={{ fontFamily: parsedData.font_style }}
            >
              <p style={{ color: parsedData.pre_title_color }}>
                {parsedData.pre_title}
              </p>
              <p style={{ color: parsedData.title_color }}>{parsedData.title}</p>
            </div>

            {/* Description */}
            {parsedData.description ? (
              <p
                className={cn(
                  "font-semibold leading-[1.49] [word-break:break-word]",
                  "text-[14px] sm:text-[16px] md:text-[16px] lg:text-[18px] xl:text-[18px]",
                )}
                style={{
                  color: parsedData.description_color,
                  fontFamily: parsedData.font_style,
                }}
              >
                {parsedData.description}
              </p>
            ) : null}
          </div>
        </RevealOnScroll>

        {/* Pills grid — 2 cols on mobile, 4 cols on desktop */}
        {pills.length > 0 ? (

            <div ref={pillsContainerRef}>
              <div
                style={{ perspective: "800px" }}
                className={cn(
                  "grid gap-[12px] w-fit",
                  "[grid-template-columns:repeat(2,max-content)]",
                  "sm:[grid-template-columns:repeat(4,max-content)]",
                )}
              >
                {pills.map((pill, index) => (
                  <div
                    key={pill.id}
                    style={{
                      animationName: "brand1-pill-xz",
                      animationDuration: "0.2s",
                      animationTimingFunction: "ease-out",
                      animationDelay: `${index * 100}ms`,
                      animationFillMode: "both",
                      animationPlayState: pillsInView ? "running" : "paused",
                    }}
                  >
                    <BrandPillCard pill={pill} />
                  </div>
                ))}
              </div>
            </div>

        ) : null}
      </div>
    </section>
  );
};

export default Brand1;
